import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { devicesApi, driversApi, groupsApi, billingApi, geocodingApi, evApi, tenantSettingsApi, privacyApi, type EvModel, type EvDeviceSnapshot, type EvSocPoint, type EvChargeSession, type PrivacyReason } from '../services/api';
import { useHasModule } from '../store/modules';
import { useDevicesStore, Device } from '../store/devices.store';
import { useNotificationsStore } from '../store/notifications.store';
import { useTenantContextStore } from '../store/tenant-context.store';
import { useAuthStore } from '../store/auth.store';
import { connectSocket } from '../services/socket';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import WorkScheduleEditor, {
  DEFAULT_WORK_SCHEDULE,
  privacySummary,
  type WorkSchedule,
} from '../components/WorkScheduleEditor';

/**
 * Closed list mirroring the server `PrivacyReason` enum. Deliberately never
 * free text: a driver typing a medical reason would disclose a GDPR special
 * category to their manager, who is shown this label.
 */
const PRIVACY_REASON_LABELS: Record<PrivacyReason, string> = {
  LEAVE: 'congés',
  WEEKLY_REST: 'repos hebdomadaire',
  PERSONAL_USE: 'usage personnel',
  OTHER: 'absence déclarée',
};
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Pencil,
  Trash2,
  MapPin,
  Wifi,
  WifiOff,
  Truck,
  Car,
  X,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Check,
  Gauge,
  Navigation,
  Clock,
  Activity,
  Loader2,
  AlertTriangle,
  User,
  Send,
  CheckCircle2,
  CreditCard,
  Fuel,
  Zap,
  BatteryCharging,
  Shield,
  ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';

type StatusFilter = 'ALL' | 'ONLINE' | 'OFFLINE';

/** Indexed by ISO weekday - 1, so index 0 is Monday. */

interface TenantPrivacyDefaults {
  timezone?: string;
  enabled: boolean;
  workSchedule: WorkSchedule | null;
}

interface DeviceFormData {
  trackerType: 'HARDWARE' | 'OEM';
  imei: string;
  vin: string;
  name: string;
  plate: string;
  brand: string;
  model: string;
  category: string;
  speedLimit: number;
  icon: string;
  markerColor: string;
  driverId: string | null;
  trackerModel: string;
  vehicleType: string;
  avgConsumption: number | '';
  evEnabled: boolean;
  evModelId: string | null;
  // Privacy mode. `privacyOverride` false means the device follows the tenant
  // defaults and the window fields below are kept but unused.
  privacyOverride: boolean;
  privacyEnabled: boolean;
  workSchedule: WorkSchedule;
  privacyTheftOverride: boolean;
}

const defaultFormData: DeviceFormData = {
  trackerType: 'HARDWARE',
  imei: '',
  vin: '',
  name: '',
  plate: '',
  brand: '',
  model: '',
  category: 'car',
  speedLimit: 120,
  icon: 'car',
  markerColor: '#3B82F6',
  driverId: null,
  trackerModel: 'FMB020',
  vehicleType: 'THERMAL',
  avgConsumption: '',
  evEnabled: false,
  evModelId: null,
  privacyOverride: false,
  privacyEnabled: false,
  workSchedule: DEFAULT_WORK_SCHEDULE,
  // Pre-filled with the usual "outside working hours" window so enabling the
  // toggle gives something sensible immediately. Wraps midnight on purpose.
  privacyTheftOverride: true,
};

const vehicleIcons = [
  { value: 'car', label: 'Voiture', icon: Car },
  { value: 'truck', label: 'Camion', icon: Truck },
];

const colorOptions = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#6B7280', // gray
];

// Teltonika FM series tracker models
const teltonikaTrackerModels = [
  // FMB Series - Basic
  { value: 'FMB001', label: 'FMB001 - OBD Tracker', category: 'OBD', features: ['OBD-II', '2G'] },
  { value: 'FMB002', label: 'FMB002 - OBD Tracker', category: 'OBD', features: ['OBD-II', '2G'] },
  { value: 'FMB003', label: 'FMB003 - OBD Tracker Advanced', category: 'OBD', features: ['OBD-II', 'OEM', '2G'] },
  { value: 'FMB010', label: 'FMB010 - OBD Tracker', category: 'OBD', features: ['OBD-II', '2G'] },
  { value: 'FMB020', label: 'FMB020 - OBD Eco Tracker', category: 'OBD', features: ['OBD-II', 'Eco-Driving', '2G'] },

  // FMB Series - Professional
  { value: 'FMB110', label: 'FMB110 - Advanced Tracker', category: 'Professional', features: ['2G', 'CAN'] },
  { value: 'FMB120', label: 'FMB120 - Advanced Tracker', category: 'Professional', features: ['2G', 'CAN', 'Bluetooth'] },
  { value: 'FMB122', label: 'FMB122 - Professional Tracker', category: 'Professional', features: ['2G', 'CAN', 'Bluetooth'] },
  { value: 'FMB125', label: 'FMB125 - Professional Tracker', category: 'Professional', features: ['2G', 'CAN', 'Bluetooth'] },
  { value: 'FMB130', label: 'FMB130 - Advanced Tracker', category: 'Professional', features: ['2G', 'CAN'] },
  { value: 'FMB140', label: 'FMB140 - Advanced Tracker', category: 'Professional', features: ['2G', 'CAN'] },

  // FMB Series - Easy
  { value: 'FMB202', label: 'FMB202 - Waterproof Tracker', category: 'Easy', features: ['2G', 'IP67'] },
  { value: 'FMB204', label: 'FMB204 - Motorcycle Tracker', category: 'Easy', features: ['2G', 'IP67', 'Moto'] },
  { value: 'FMB208', label: 'FMB208 - Compact Tracker', category: 'Easy', features: ['2G', 'Compact'] },
  { value: 'FMB209', label: 'FMB209 - Hidden Tracker', category: 'Easy', features: ['2G', 'Hidden'] },

  // FMB Series - GNSS
  { value: 'FMB900', label: 'FMB900 - Entry Level', category: 'GNSS', features: ['2G', 'Basic'] },
  { value: 'FMB910', label: 'FMB910 - Smart Tracker', category: 'GNSS', features: ['2G', 'Bluetooth'] },
  { value: 'FMB920', label: 'FMB920 - Smart Tracker', category: 'GNSS', features: ['2G', 'Bluetooth', 'CAN'] },
  { value: 'FMB930', label: 'FMB930 - Advanced Tracker', category: 'GNSS', features: ['2G', 'RS232/RS485'] },
  { value: 'FMB962', label: 'FMB962 - OBDII Tracker', category: 'GNSS', features: ['2G', 'OBD-II', 'CAN'] },
  { value: 'FMB964', label: 'FMB964 - OBDII Tracker', category: 'GNSS', features: ['2G', 'OBD-II', 'CAN'] },

  // FMC Series - 4G/LTE
  { value: 'FMC001', label: 'FMC001 - 4G OBD Tracker', category: '4G OBD', features: ['OBD-II', '4G LTE'] },
  { value: 'FMC003', label: 'FMC003 - 4G OBD Advanced', category: '4G OBD', features: ['OBD-II', 'OEM', '4G LTE'] },
  { value: 'FMC125', label: 'FMC125 - 4G Professional', category: '4G Pro', features: ['4G LTE', 'CAN', 'Bluetooth'] },
  { value: 'FMC130', label: 'FMC130 - 4G Advanced', category: '4G Pro', features: ['4G LTE', 'CAN'] },
  { value: 'FMC150', label: 'FMC150 - 4G LTE Cat1', category: '4G Pro', features: ['4G LTE Cat1', 'CAN'] },
  { value: 'FMC230', label: 'FMC230 - 4G LTE Cat M1', category: '4G Pro', features: ['4G LTE Cat M1', 'CAN'] },
  { value: 'FMC640', label: 'FMC640 - 4G Advanced', category: '4G Pro', features: ['4G LTE', 'CAN', 'RS232'] },
  { value: 'FMC650', label: 'FMC650 - 4G HD Camera', category: '4G Pro', features: ['4G LTE', 'Camera', 'ADAS'] },
  { value: 'FMC880', label: 'FMC880 - 4G Fleet', category: '4G Pro', features: ['4G LTE', 'CAN FMS'] },

  // FMM Series - LTE Cat M1
  { value: 'FMM001', label: 'FMM001 - Cat M1 OBD', category: 'Cat M1', features: ['OBD-II', 'LTE Cat M1'] },
  { value: 'FMM003', label: 'FMM003 - Cat M1 OBD Advanced', category: 'Cat M1', features: ['OBD-II', 'OEM', 'LTE Cat M1'] },
  { value: 'FMM125', label: 'FMM125 - Cat M1 Professional', category: 'Cat M1', features: ['LTE Cat M1', 'CAN'] },
  { value: 'FMM130', label: 'FMM130 - Cat M1 Advanced', category: 'Cat M1', features: ['LTE Cat M1', 'CAN'] },
  { value: 'FMM230', label: 'FMM230 - Cat M1 Advanced', category: 'Cat M1', features: ['LTE Cat M1', 'CAN'] },
  { value: 'FMM640', label: 'FMM640 - Cat M1 Fleet', category: 'Cat M1', features: ['LTE Cat M1', 'RS232'] },

  // FM Series - Basic
  { value: 'FM3001', label: 'FM3001 - 3G OBD', category: '3G OBD', features: ['OBD-II', '3G'] },
  { value: 'FM3612', label: 'FM3612 - 3G Tracker', category: '3G', features: ['3G', 'Bluetooth'] },
  { value: 'FM6320', label: 'FM6320 - 3G Advanced', category: '3G', features: ['3G', 'CAN', 'Bluetooth'] },

  // TAT Series - Asset Trackers
  { value: 'TAT100', label: 'TAT100 - Asset Tracker', category: 'Asset', features: ['2G', 'Long Battery'] },
  { value: 'TAT140', label: 'TAT140 - Asset Tracker 4G', category: 'Asset', features: ['4G LTE', 'Long Battery'] },
  { value: 'TAT141', label: 'TAT141 - Asset Tracker 4G', category: 'Asset', features: ['4G LTE', 'Long Battery'] },

  // TMT Series - Personal Trackers
  { value: 'TMT250', label: 'TMT250 - Personal Tracker', category: 'Personal', features: ['2G', 'SOS', 'Compact'] },

  // GH Series - Personal
  { value: 'GH5200', label: 'GH5200 - Personal Tracker', category: 'Personal', features: ['2G', 'SOS', 'Fall Detection'] },

  // Other
  { value: 'OTHER', label: 'OTHER', category: 'Other', features: [] },
];

interface TenantSubscription {
  id: string;
  deviceIds?: string[];
  status: string;
  expiresAt?: string;
  currentPeriodEnd?: string;
  plan: {
    id: string;
    name: string;
    displayName: string;
  };
}

export default function DevicesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { devices, setDevices, setLoading, setError } = useDevicesStore();
  const { selectedTenant } = useTenantContextStore();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  // Tenant ID for query keys - forces refetch when tenant changes
  const tenantKey = selectedTenant?.id || 'default';

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [groupFilter, setGroupFilter] = useState<string>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [formData, setFormData] = useState<DeviceFormData>(defaultFormData);
  const [formErrors, setFormErrors] = useState<Partial<DeviceFormData>>({});
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState<{ top: number; left: number } | null>(null);

  // EV module: only fetch the catalog when the tenant has the module enabled.
  const hasEvModule = useHasModule('EV');
  const { data: evModels = [] } = useQuery<EvModel[]>({
    queryKey: ['ev-models'],
    queryFn: () => evApi.listModels({ activeOnly: true }),
    enabled: hasEvModule,
    staleTime: 5 * 60 * 1000,
  });

  // Odometer modal state
  const [isOdometerModalOpen, setIsOdometerModalOpen] = useState(false);
  const [odometerDevice, setOdometerDevice] = useState<Device | null>(null);
  const [odometerValue, setOdometerValue] = useState('');
  const [odometerLoading, setOdometerLoading] = useState(false);
  const [odometerResult, setOdometerResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const itemsPerPage = 10;

  // Fetch devices
  const { isLoading, data, error } = useQuery({
    queryKey: ['devices', tenantKey],
    queryFn: async () => {
      const response = await devicesApi.list({ limit: 1000 });
      return response.data;
    },
  });

  // Fetch groups for filter
  const { data: groupsData } = useQuery({
    queryKey: ['groups', tenantKey],
    queryFn: async () => {
      const response = await groupsApi.list();
      return response.data;
    },
  });
  const groups = Array.isArray(groupsData?.data) ? groupsData.data : [];

  // Fetch usage/limits
  const { data: usageData } = useQuery({
    queryKey: ['billing-usage', tenantKey],
    queryFn: async () => {
      const response = await billingApi.getUsage();
      return response.data;
    },
  });

  // Fetch tenant subscriptions to link devices to subscriptions
  const { data: subscriptionsData } = useQuery({
    queryKey: ['billing-subscriptions', tenantKey],
    queryFn: async () => {
      const response = await billingApi.getSubscriptions();
      return response.data;
    },
  });
  const tenantSubscriptions: TenantSubscription[] = Array.isArray(subscriptionsData) ? subscriptionsData : [];

  // Build a map: deviceId -> subscription
  const deviceSubscriptionMap = useMemo(() => {
    const map = new Map<string, TenantSubscription>();
    for (const sub of tenantSubscriptions) {
      if (sub.deviceIds && Array.isArray(sub.deviceIds)) {
        for (const did of sub.deviceIds) {
          map.set(did, sub);
        }
      }
    }
    return map;
  }, [tenantSubscriptions]);

  // Check if device limit is reached
  const deviceLimitReached = usageData?.devices
    ? usageData.devices.limit > 0 && usageData.devices.used >= usageData.devices.limit
    : false;

  // Update store when data changes
  useEffect(() => {
    if (data?.data) {
      setDevices(data.data);
    }
  }, [data, setDevices]);

  // Update error in store
  useEffect(() => {
    if (error) {
      setError((error as Error).message);
    }
  }, [error, setError]);

  // Connect to WebSocket
  useEffect(() => {
    try {
      connectSocket();
    } catch (err) {
      console.error('Failed to connect WebSocket:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  // Create device mutation
  const addNotification = useNotificationsStore((state) => state.addNotification);

  const createMutation = useMutation({
    mutationFn: (data: DeviceFormData) =>
      devicesApi.create({
        // OEM Connect: no IMEI — the VIN identifies the vehicle (clearance +
        // synthetic IMEI happen server-side). Otherwise a normal hardware tracker.
        ...(data.trackerType === 'OEM'
          ? { oemProvider: 'HIGH_MOBILITY', vin: data.vin }
          : { imei: data.imei, trackerModel: data.trackerModel || undefined }),
        name: data.name,
        plate: data.plate || undefined,
        brand: data.brand || undefined,
        model: data.model || undefined,
        speedLimit: data.speedLimit,
        icon: data.icon,
        markerColor: data.markerColor,
        vehicleType: data.vehicleType,
        avgConsumption: data.avgConsumption ? Number(data.avgConsumption) : undefined,
        evEnabled: data.evEnabled,
        evModelId: data.evEnabled ? data.evModelId : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', tenantKey] });
      setIsCreateModalOpen(false);
      setFormData(defaultFormData);
      addNotification({
        type: 'success',
        title: t('devices.toast_created_title'),
        message: t('devices.toast_created_msg'),
      });
    },
    onError: (error: any) => {
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.error;

      if (errorCode === 'LIMIT_REACHED') {
        addNotification({
          type: 'warning',
          title: t('devices.toast_limit_title'),
          message: t('devices.toast_limit_msg'),
        });
      } else if (errorCode === 'IMEI_EXISTS') {
        addNotification({
          type: 'warning',
          title: t('devices.toast_imei_exists_title'),
          message: t('devices.toast_imei_exists_msg'),
        });
      } else if (errorCode === 'FORBIDDEN') {
        addNotification({
          type: 'alert',
          title: t('devices.toast_forbidden_title'),
          message: t('devices.toast_forbidden_msg'),
        });
      } else {
        addNotification({
          type: 'alert',
          title: t('devices.toast_error_title'),
          message: errorMessage || t('devices.toast_create_failed'),
        });
      }
    },
  });

  // Tenant privacy defaults — shown in the device modal so an admin can see
  // what an inheriting vehicle actually follows. ADMIN-only endpoint.
  const { data: privacyDefaults } = useQuery({
    queryKey: ['tenant-privacy-defaults', tenantKey],
    queryFn: async () => {
      const res = await tenantSettingsApi.getPrivacyDefaults();
      return res.data as TenantPrivacyDefaults;
    },
    enabled: isAdmin,
  });

  // Running declared periods, so the badge can say WHY a vehicle is private.
  // A vehicle private because of the recurring nightly window has no entry —
  // that is the configured schedule, not a declaration, and needs no reason.
  const { data: activePrivacyPeriods } = useQuery({
    queryKey: ['privacy-active', tenantKey],
    queryFn: privacyApi.getActive,
    retry: false,
  });

  const privacyReasonByDevice = useMemo(() => {
    const map = new Map<string, { reason: PrivacyReason; endsAt: string }>();
    for (const period of activePrivacyPeriods ?? []) {
      map.set(period.deviceId, { reason: period.reason, endsAt: period.endsAt });
    }
    return map;
  }, [activePrivacyPeriods]);

  /**
   * Tooltip for the status pill. The reason comes from a closed list, which is
   * exactly what makes it safe to surface to a manager: a driver can never put
   * a medical detail in it.
   */
  const privacyTooltip = (device: { id: string; privacyActive?: boolean }) => {
    if (!device.privacyActive) return undefined;
    const declared = privacyReasonByDevice.get(device.id);
    if (!declared) return "Mode vie privée actif — aucune position n'est enregistrée";
    return `Mode vie privée actif — ${PRIVACY_REASON_LABELS[declared.reason]}, jusqu'au ${format(
      new Date(declared.endsAt),
      'd MMMM à HH:mm',
      { locale: fr }
    )}. Aucune position n'est enregistrée.`;
  };

  // Update device mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<DeviceFormData> }) => {
      const result = await devicesApi.update(id, {
        name: data.name,
        plate: data.plate || undefined,
        brand: data.brand || undefined,
        model: data.model || undefined,
        speedLimit: data.speedLimit,
        driverId: data.driverId,
        trackerModel: data.trackerModel || undefined,
        icon: data.icon,
        markerColor: data.markerColor,
        vehicleType: data.vehicleType,
        avgConsumption: data.avgConsumption ? Number(data.avgConsumption) : undefined,
        evEnabled: data.evEnabled,
        evModelId: data.evEnabled ? data.evModelId ?? null : null,
      });

      // Privacy sits behind its own ADMIN-only endpoint. Only call it when the
      // values actually changed, so an ordinary save doesn't fill the audit log
      // with no-op privacy entries.
      const nextPrivacy = {
        override: data.privacyOverride ?? false,
        enabled: data.privacyEnabled ?? false,
        workSchedule: data.workSchedule ?? DEFAULT_WORK_SCHEDULE,
        theftOverride: data.privacyTheftOverride ?? true,
      };
      const currentPrivacy = {
        override: selectedDevice?.privacyOverride ?? false,
        enabled: selectedDevice?.privacyEnabled ?? false,
        workSchedule: (selectedDevice?.workSchedule as WorkSchedule) ?? DEFAULT_WORK_SCHEDULE,
        theftOverride: selectedDevice?.privacyTheftOverride ?? true,
      };

      if (isAdmin && JSON.stringify(nextPrivacy) !== JSON.stringify(currentPrivacy)) {
        await devicesApi.updatePrivacy(id, nextPrivacy);
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', tenantKey] });
      setIsEditModalOpen(false);
      setSelectedDevice(null);
      setFormData(defaultFormData);
    },
  });

  // Delete device mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => devicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', tenantKey] });
      setIsDeleteModalOpen(false);
      setSelectedDevice(null);
    },
  });

  // Fetch drivers for assignment
  const { data: driversData } = useQuery({
    queryKey: ['drivers', tenantKey],
    queryFn: async () => {
      const response = await driversApi.list({ isActive: true, limit: 100 });
      return response.data;
    },
  });
  const drivers = Array.isArray(driversData?.data) ? driversData.data : [];

  // Filter and search devices
  const filteredDevices = useMemo(() => {
    return devices.filter((device) => {
      // Status filter
      if (statusFilter !== 'ALL' && device.status !== statusFilter) {
        return false;
      }

      // Group filter
      if (groupFilter !== 'ALL') {
        if (groupFilter === 'NONE' && device.group) {
          return false;
        } else if (groupFilter !== 'NONE' && device.group?.id !== groupFilter) {
          return false;
        }
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          device.name.toLowerCase().includes(query) ||
          device.imei.includes(query) ||
          device.plate?.toLowerCase().includes(query) ||
          device.brand?.toLowerCase().includes(query) ||
          device.model?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [devices, statusFilter, groupFilter, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredDevices.length / itemsPerPage);
  const paginatedDevices = filteredDevices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Stats
  const stats = {
    total: devices.length,
    online: devices.filter((d) => d.status === 'ONLINE').length,
    offline: devices.filter((d) => d.status === 'OFFLINE').length,
  };

  // Handlers
  const handleOpenCreate = () => {
    setFormData(defaultFormData);
    setFormErrors({});
    setIsCreateModalOpen(true);
  };

  const handleOpenEdit = (device: Device) => {
    setSelectedDevice(device);
    setFormData({
      trackerType: (device as any).oemProvider ? 'OEM' : 'HARDWARE',
      imei: device.imei,
      vin: (device as any).vin || '',
      name: device.name,
      plate: device.plate || '',
      brand: device.brand || '',
      model: device.model || '',
      category: device.category || 'car',
      speedLimit: device.speedLimit || 120,
      icon: device.icon || 'car',
      markerColor: device.markerColor || '#3B82F6',
      driverId: (device as any).driverId || null,
      trackerModel: (device as any).trackerModel || 'FMB020',
      vehicleType: device.vehicleType || 'THERMAL',
      avgConsumption: device.avgConsumption || '',
      evEnabled: device.evEnabled ?? false,
      evModelId: device.evModelId ?? null,
      privacyOverride: device.privacyOverride ?? false,
      privacyEnabled: device.privacyEnabled ?? false,
      workSchedule: (device.workSchedule as WorkSchedule) ?? DEFAULT_WORK_SCHEDULE,
      privacyTheftOverride: device.privacyTheftOverride ?? true,
    });
    setFormErrors({});
    setIsEditModalOpen(true);
    setActionMenuId(null);
  };

  const handleOpenDelete = (device: Device) => {
    setSelectedDevice(device);
    setIsDeleteModalOpen(true);
    setActionMenuId(null);
  };

  const handleOpenDetail = (device: Device) => {
    setSelectedDevice(device);
    setIsDetailModalOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: Partial<DeviceFormData> = {};

    if (formData.trackerType === 'OEM') {
      if (!formData.vin || formData.vin.length !== 17) {
        errors.vin = 'VIN doit contenir 17 caractères';
      }
    } else if (!formData.imei || formData.imei.length !== 15) {
      errors.imei = 'IMEI doit contenir 15 chiffres';
    }

    if (!formData.name || formData.name.length < 2) {
      errors.name = 'Nom requis (min 2 caracteres)';
    }

    if (formData.speedLimit < 0 || formData.speedLimit > 300) {
      errors.speedLimit = 'Vitesse entre 0 et 300 km/h' as unknown as number;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      createMutation.mutate(formData);
    }
  };

  const handleSubmitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDevice && validateForm()) {
      updateMutation.mutate({ id: selectedDevice.id, data: formData });
    }
  };

  const handleDelete = () => {
    if (selectedDevice) {
      deleteMutation.mutate(selectedDevice.id);
    }
  };

  // Odometer handlers
  const handleOpenOdometer = (device: Device) => {
    setOdometerDevice(device);
    setOdometerValue('');
    setOdometerResult(null);
    setIsOdometerModalOpen(true);
    setActionMenuId(null);
    setActionMenuPos(null);
  };

  const handleSendOdometer = async () => {
    if (!odometerDevice || !odometerValue) return;
    const km = parseInt(odometerValue, 10);
    if (isNaN(km) || km < 0) {
      setOdometerResult({ type: 'error', text: t('devices.odometer_invalid_number') });
      return;
    }
    setOdometerLoading(true);
    setOdometerResult(null);
    try {
      const res = await devicesApi.setOdometer(odometerDevice.id, km);
      setOdometerResult({ type: 'success', text: res.data.message || t('devices.odometer_command_sent', { km }) });
    } catch (error: any) {
      const msg = error.response?.data?.error || t('devices.odometer_send_error');
      setOdometerResult({ type: 'error', text: msg });
    } finally {
      setOdometerLoading(false);
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, groupFilter]);

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Limit reached warning */}
      {deviceLimitReached && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">
              {t('devices.limit_warning_title', { used: usageData?.devices?.used, limit: usageData?.devices?.limit })}
            </p>
            <p className="text-sm text-amber-600">
              {t('devices.limit_warning_body')}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('devices.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('devices.summary', { count: stats.total, total: stats.total, online: stats.online })}
            {usageData?.devices?.limit > 0 && (
              <span className="ml-2 text-gray-400">
                • {t('devices.limit_label', { limit: usageData.devices.limit })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative group">
            <button
              onClick={handleOpenCreate}
              disabled={deviceLimitReached}
              className={clsx(
                "inline-flex items-center px-4 py-2 rounded-lg transition-colors",
                deviceLimitReached
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-primary-600 text-white hover:bg-primary-700"
              )}
            >
              <Plus className="h-5 w-5 mr-2" />
              {t('devices.add')}
            </button>
            {deviceLimitReached && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <span>{t('devices.limit_reached', { used: usageData?.devices?.used, limit: usageData?.devices?.limit })}</span>
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('devices.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">{t('devices.filter_all_statuses')}</option>
            <option value="ONLINE">{t('devices.filter_online')}</option>
            <option value="OFFLINE">{t('devices.filter_offline')}</option>
          </select>
        </div>

        {/* Group filter */}
        <div className="flex items-center gap-2">
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">{t('devices.filter_all_groups')}</option>
            <option value="NONE">{t('devices.filter_no_group')}</option>
            {groups.map((group: { id: string; name: string; color: string }) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div>
          <table className="w-full divide-y divide-gray-200 table-fixed md:table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-full md:w-auto px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('devices.th_vehicle')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  {t('devices.th_imei')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  {t('devices.th_status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  {t('devices.th_speed')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  {t('devices.th_last_position')}
                </th>
                <th className="w-20 md:w-auto px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('devices.th_actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedDevices.map((device) => (
                <tr
                  key={device.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleOpenDetail(device)}
                >
                  <td className="px-3 py-3 md:px-4 md:py-4">
                    <div className="flex items-center">
                      <div
                        className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center"
                        style={{ backgroundColor: `${device.markerColor}20` }}
                      >
                        {device.icon === 'truck' ? (
                          <Truck className="h-5 w-5" style={{ color: device.markerColor }} />
                        ) : (
                          <Car className="h-5 w-5" style={{ color: device.markerColor }} />
                        )}
                      </div>
                      <div className="ml-3 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{device.name}</div>
                        <div className="text-sm text-gray-500 truncate">
                          {device.plate || '-'}
                          {device.brand && ` - ${device.brand}`}
                          {device.model && ` ${device.model}`}
                        </div>
                        <div className="text-xs text-gray-400 md:hidden mt-0.5">
                          {device.privacyActive
                            ? '\u{1F512}'
                            : device.status === 'ONLINE'
                              ? '\u{1F7E2}'
                              : '\u26AA'}{' '}
                          {device.lastPosition ? `${Math.round(device.lastPosition.speed)} km/h` : ''}{' '}
                          {device.lastPositionAt
                            ? formatDistanceToNow(new Date(device.lastPositionAt), { addSuffix: true, locale: fr })
                            : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 font-mono hidden md:table-cell">
                    {device.imei}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap hidden md:table-cell">
                    {/* Privacy wins over online/offline: it explains the silence,
                        so the manager doesn't open a ticket for a dead tracker. */}
                    <span
                      title={privacyTooltip(device)}
                      className={clsx(
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        device.privacyActive
                          ? 'bg-indigo-100 text-indigo-800'
                          : device.status === 'ONLINE'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                      )}
                    >
                      {device.privacyActive ? (
                        <Shield className="h-3 w-3 mr-1" />
                      ) : device.status === 'ONLINE' ? (
                        <Wifi className="h-3 w-3 mr-1" />
                      ) : (
                        <WifiOff className="h-3 w-3 mr-1" />
                      )}
                      {device.privacyActive
                        ? 'Confidentialité'
                        : device.status === 'ONLINE'
                          ? 'En ligne'
                          : 'Hors ligne'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                    {device.lastPosition ? (
                      <span
                        className={clsx(
                          device.lastPosition.speed > (device.speedLimit || 120) &&
                            'text-red-600 font-medium'
                        )}
                      >
                        {Math.round(device.lastPosition.speed)} km/h
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                    {device.lastPositionAt ? (
                      <span title={new Date(device.lastPositionAt).toLocaleString('fr-FR')}>
                        {formatDistanceToNow(new Date(device.lastPositionAt), {
                          addSuffix: true,
                          locale: fr,
                        })}
                      </span>
                    ) : (
                      'Jamais'
                    )}
                  </td>
                  <td className="px-2 py-3 md:px-4 md:py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (actionMenuId === device.id) {
                          setActionMenuId(null);
                          setActionMenuPos(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const menuHeight = 140; // hauteur approximative du menu (3 items)
                          const spaceBelow = window.innerHeight - rect.bottom;
                          setActionMenuId(device.id);
                          setActionMenuPos({
                            top: spaceBelow < menuHeight ? rect.top - menuHeight - 4 : rect.bottom + 4,
                            left: rect.right - 192,
                          });
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                    >
                      <MoreVertical className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
              {paginatedDevices.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    {searchQuery || statusFilter !== 'ALL' || groupFilter !== 'ALL' ? (
                      <div>
                        <Search className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                        <p>{t('devices.empty_search')}</p>
                      </div>
                    ) : (
                      <div>
                        <Truck className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                        <p>{t('devices.empty_no_devices')}</p>
                        <button
                          onClick={handleOpenCreate}
                          className="mt-4 text-primary-600 hover:text-primary-700"
                        >
                          {t('devices.add_first')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              {t('devices.pagination', {
                start: (currentPage - 1) * itemsPerPage + 1,
                end: Math.min(currentPage * itemsPerPage, filteredDevices.length),
                total: filteredDevices.length,
              })}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={clsx(
                    'px-3 py-1 rounded-lg text-sm',
                    page === currentPage
                      ? 'bg-primary-600 text-white'
                      : 'hover:bg-gray-100 text-gray-700'
                  )}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action dropdown - rendu en fixed pour ne pas etre clippe par overflow-hidden du tableau */}
      {actionMenuId && actionMenuPos && (() => {
        const menuDevice = devices.find(d => d.id === actionMenuId);
        if (!menuDevice) return null;
        return (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={(e) => {
                e.stopPropagation();
                setActionMenuId(null);
                setActionMenuPos(null);
              }}
            />
            <div
              className="fixed w-48 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-20"
              style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDetail(menuDevice);
                  setActionMenuId(null);
                  setActionMenuPos(null);
                }}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg"
              >
                <Activity className="h-4 w-4 mr-2" />
                {t('devices.menu_view_details')}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(menuDevice);
                  setActionMenuPos(null);
                }}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Pencil className="h-4 w-4 mr-2" />
                {t('devices.menu_edit')}
              </button>
              {isAdmin && menuDevice.simIccid && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenOdometer(menuDevice);
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Gauge className="h-4 w-4 mr-2" />
                  {t('devices.menu_odometer')}
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDelete(menuDevice);
                    setActionMenuPos(null);
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('devices.menu_delete')}
                </button>
              )}
            </div>
          </>
        );
      })()}

      {/* Create Modal */}
      {isCreateModalOpen && (
        <DeviceFormModal
          title={t('devices.title_add')}
          formData={formData}
          setFormData={setFormData}
          formErrors={formErrors}
          onSubmit={handleSubmitCreate}
          onClose={() => setIsCreateModalOpen(false)}
          isLoading={createMutation.isPending}
          submitLabel={t('devices.submit_add')}
          isCreate
          evModule={hasEvModule ? { models: evModels } : undefined}
        />
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedDevice && (
        <DeviceFormModal
          title={t('devices.title_edit')}
          formData={formData}
          setFormData={setFormData}
          formErrors={formErrors}
          onSubmit={handleSubmitEdit}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedDevice(null);
          }}
          isLoading={updateMutation.isPending}
          submitLabel={t('devices.submit_save')}
          drivers={drivers}
          evModule={hasEvModule ? { models: evModels } : undefined}
          privacy={{ canEdit: isAdmin, defaults: privacyDefaults }}
        />
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && selectedDevice && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setIsDeleteModalOpen(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                {t('devices.delete_title')}
              </h3>
              <p className="text-center text-gray-500 mb-6">
                {t('devices.delete_confirm', { name: selectedDevice.name })}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('devices.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? t('devices.submit_deleting') : t('devices.menu_delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {isDetailModalOpen && selectedDevice && (
        <DeviceDetailModal
          device={selectedDevice}
          subscription={deviceSubscriptionMap.get(selectedDevice.id) || null}
          privacyExplanation={privacyTooltip(selectedDevice)}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedDevice(null);
          }}
          onEdit={() => {
            setIsDetailModalOpen(false);
            handleOpenEdit(selectedDevice);
          }}
        />
      )}

      {/* Odometer Modal */}
      {isOdometerModalOpen && odometerDevice && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setIsOdometerModalOpen(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Gauge className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('devices.odometer_modal_title')}</h3>
                    <p className="text-sm text-gray-500">{odometerDevice.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOdometerModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('devices.odometer_label')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      max={9999999}
                      value={odometerValue}
                      onChange={(e) => setOdometerValue(e.target.value)}
                      placeholder={t('devices.odometer_placeholder_short')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                      onKeyDown={(e) => e.key === 'Enter' && handleSendOdometer()}
                      autoFocus
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">
                      km
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('devices.odometer_help')}
                  </p>
                </div>

                {/* Result */}
                {odometerResult && (
                  <div className={clsx(
                    'p-3 rounded-lg flex items-center gap-2 text-sm',
                    odometerResult.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  )}>
                    {odometerResult.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                    {odometerResult.text}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
                <button
                  onClick={() => setIsOdometerModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  {t('devices.close')}
                </button>
                <button
                  onClick={handleSendOdometer}
                  disabled={odometerLoading || !odometerValue}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {odometerLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('devices.sending')}
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      {t('devices.send')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// Driver type for the form
interface DriverOption {
  id: string;
  name: string;
  phone: string | null;
}

// Device Form Modal Component
function DeviceFormModal({
  title,
  formData,
  setFormData,
  formErrors,
  onSubmit,
  onClose,
  isLoading,
  submitLabel,
  isCreate = false,
  drivers = [],
  evModule,
  privacy,
}: {
  title: string;
  formData: DeviceFormData;
  setFormData: (data: DeviceFormData) => void;
  formErrors: Partial<DeviceFormData>;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  isLoading: boolean;
  submitLabel: string;
  isCreate?: boolean;
  drivers?: DriverOption[];
  evModule?: { models: EvModel[] };
  privacy?: { canEdit: boolean; defaults?: TenantPrivacyDefaults };
}) {
  const { t } = useTranslation();
  const [privacyOpen, setPrivacyOpen] = useState(false);

  // Collapsed-header summary: what is actually applied, override or inherited.
  const effectivePrivacy = formData.privacyOverride
    ? { enabled: formData.privacyEnabled, schedule: formData.workSchedule }
    : {
        enabled: privacy?.defaults?.enabled ?? false,
        schedule: (privacy?.defaults?.workSchedule as WorkSchedule) ?? null,
      };
  const privacySummaryText =
    effectivePrivacy.enabled && effectivePrivacy.schedule
      ? privacySummary(effectivePrivacy.schedule)
      : 'Désactivé';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit}>
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Tracker type (create only) */}
              {isCreate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de suivi</label>
                  <select
                    value={formData.trackerType}
                    onChange={(e) => setFormData({ ...formData, trackerType: e.target.value as 'HARDWARE' | 'OEM' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="HARDWARE">Boîtier GPS (IMEI)</option>
                    <option value="OEM">OEM Connect — Données constructeur (VIN)</option>
                  </select>
                </div>
              )}

              {/* VIN (OEM Connect) */}
              {formData.trackerType === 'OEM' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                  <input
                    type="text"
                    value={formData.vin}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase().replace(/\s/g, '') })}
                    maxLength={17}
                    placeholder="VF1XXXXXXXXXXXXXX"
                    disabled={!isCreate}
                    className={clsx(
                      'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono',
                      formErrors.vin ? 'border-red-500' : 'border-gray-300',
                      !isCreate && 'bg-gray-100 cursor-not-allowed'
                    )}
                  />
                  {formErrors.vin && <p className="mt-1 text-sm text-red-500">{formErrors.vin}</p>}
                  <p className="mt-1 text-xs text-gray-500">
                    17 caractères. Le véhicule remonte ses données via le constructeur, sans boîtier.
                  </p>
                </div>
              )}

              {formData.trackerType !== 'OEM' && (
                <>
              {/* IMEI */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('devices.field_imei_label')}
                </label>
                <input
                  type="text"
                  value={formData.imei}
                  onChange={(e) =>
                    setFormData({ ...formData, imei: e.target.value.replace(/\D/g, '') })
                  }
                  maxLength={15}
                  placeholder="352625090000001"
                  disabled={!isCreate}
                  className={clsx(
                    'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                    formErrors.imei ? 'border-red-500' : 'border-gray-300',
                    !isCreate && 'bg-gray-100 cursor-not-allowed'
                  )}
                />
                {formErrors.imei && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.imei}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {t('devices.field_imei_chars', { count: formData.imei.length })}
                </p>
              </div>

              {/* Tracker Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('devices.field_tracker_model')}
                </label>
                <select
                  value={formData.trackerModel}
                  onChange={(e) => setFormData({ ...formData, trackerModel: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <optgroup label={t('devices.optgroup_obd')}>
                    {teltonikaTrackerModels.filter(tm => tm.category === 'OBD').map(tm => (
                      <option key={tm.value} value={tm.value}>{tm.value === 'OTHER' ? t('devices.tracker_model_other') : tm.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label={t('devices.optgroup_professional')}>
                    {teltonikaTrackerModels.filter(tm => tm.category === 'Professional').map(tm => (
                      <option key={tm.value} value={tm.value}>{tm.value === 'OTHER' ? t('devices.tracker_model_other') : tm.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label={t('devices.optgroup_gnss')}>
                    {teltonikaTrackerModels.filter(tm => tm.category === 'GNSS').map(tm => (
                      <option key={tm.value} value={tm.value}>{tm.value === 'OTHER' ? t('devices.tracker_model_other') : tm.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label={t('devices.optgroup_4g_obd')}>
                    {teltonikaTrackerModels.filter(tm => tm.category === '4G OBD').map(tm => (
                      <option key={tm.value} value={tm.value}>{tm.value === 'OTHER' ? t('devices.tracker_model_other') : tm.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label={t('devices.optgroup_4g_pro')}>
                    {teltonikaTrackerModels.filter(tm => tm.category === '4G Pro').map(tm => (
                      <option key={tm.value} value={tm.value}>{tm.value === 'OTHER' ? t('devices.tracker_model_other') : tm.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label={t('devices.optgroup_cat_m1')}>
                    {teltonikaTrackerModels.filter(tm => tm.category === 'Cat M1').map(tm => (
                      <option key={tm.value} value={tm.value}>{tm.value === 'OTHER' ? t('devices.tracker_model_other') : tm.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label={t('devices.optgroup_asset')}>
                    {teltonikaTrackerModels.filter(tm => tm.category === 'Asset').map(tm => (
                      <option key={tm.value} value={tm.value}>{tm.value === 'OTHER' ? t('devices.tracker_model_other') : tm.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label={t('devices.optgroup_other')}>
                    {teltonikaTrackerModels.filter(tm => ['Easy', 'Personal', '3G', '3G OBD', 'Other'].includes(tm.category)).map(tm => (
                      <option key={tm.value} value={tm.value}>{tm.value === 'OTHER' ? t('devices.tracker_model_other') : tm.label}</option>
                    ))}
                  </optgroup>
                </select>
                {formData.trackerModel && formData.trackerModel !== 'OTHER' && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {teltonikaTrackerModels.find(tm => tm.value === formData.trackerModel)?.features.map((feat, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-700">
                        {feat}
                      </span>
                    ))}
                  </div>
                )}
              </div>
                </>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('devices.field_name_label')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Camion 1"
                  className={clsx(
                    'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                    formErrors.name ? 'border-red-500' : 'border-gray-300'
                  )}
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                )}
              </div>

              {/* Plate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('devices.field_plate')}
                </label>
                <input
                  type="text"
                  value={formData.plate}
                  onChange={(e) =>
                    setFormData({ ...formData, plate: e.target.value.toUpperCase() })
                  }
                  placeholder="AB-123-CD"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Brand & Model */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('devices.field_brand')}
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="Renault"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('devices.field_model')}
                  </label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="Master"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Speed Limit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('devices.field_speed_limit')}
                </label>
                <input
                  type="number"
                  min={0}
                  max={300}
                  value={formData.speedLimit}
                  onChange={(e) =>
                    setFormData({ ...formData, speedLimit: parseInt(e.target.value) || 0 })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Vehicle Type (Thermal / Electric) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('devices.field_motorization')}
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, vehicleType: 'THERMAL' })}
                    className={clsx(
                      'flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors',
                      formData.vehicleType === 'THERMAL'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Fuel className={clsx('h-5 w-5', formData.vehicleType === 'THERMAL' ? 'text-amber-600' : 'text-gray-400')} />
                    <span className={clsx('text-sm font-medium', formData.vehicleType === 'THERMAL' ? 'text-amber-700' : 'text-gray-500')}>
                      {t('devices.motor_thermal')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, vehicleType: 'ELECTRIC' })}
                    className={clsx(
                      'flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors',
                      formData.vehicleType === 'ELECTRIC'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Zap className={clsx('h-5 w-5', formData.vehicleType === 'ELECTRIC' ? 'text-green-600' : 'text-gray-400')} />
                    <span className={clsx('text-sm font-medium', formData.vehicleType === 'ELECTRIC' ? 'text-green-700' : 'text-gray-500')}>
                      {t('devices.motor_electric')}
                    </span>
                  </button>
                </div>
              </div>

              {/* Average Consumption */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('devices.field_avg_consumption', { unit: formData.vehicleType === 'ELECTRIC' ? 'kWh/100km' : 'L/100km' })}
                </label>
                <div className="relative">
                  {formData.vehicleType === 'ELECTRIC' ? (
                    <Zap className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                  ) : (
                    <Fuel className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500" />
                  )}
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={formData.avgConsumption}
                    onChange={(e) =>
                      setFormData({ ...formData, avgConsumption: e.target.value ? parseFloat(e.target.value) : '' })
                    }
                    placeholder={formData.vehicleType === 'ELECTRIC' ? 'Ex: 16.5' : 'Ex: 7.2'}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* EV module — only when tenant has the EV add-on enabled */}
              {evModule && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.evEnabled}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          evEnabled: e.target.checked,
                          evModelId: e.target.checked ? formData.evModelId : null,
                        })
                      }
                      className="mt-0.5 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="flex-1">
                      <span className="flex items-center gap-2 text-sm font-medium text-emerald-900">
                        <BatteryCharging className="h-4 w-4" />
                        {t('devices.ev_module_label')}
                      </span>
                      <span className="block text-xs text-emerald-800/80 mt-0.5">
                        {t('devices.ev_module_description')}
                      </span>
                    </span>
                  </label>

                  {formData.evEnabled && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('devices.ev_model_label')}
                      </label>
                      <select
                        value={formData.evModelId ?? ''}
                        onChange={(e) =>
                          setFormData({ ...formData, evModelId: e.target.value || null })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      >
                        <option value="">{t('devices.ev_model_select')}</option>
                        {evModule.models.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.make} {m.model}
                            {m.variant ? ` ${m.variant}` : ''}
                            {m.yearFrom ? ` (${m.yearFrom}${m.yearTo ? `–${m.yearTo}` : '+'})` : ''}
                            {' · '}
                            {m.batteryKwh} kWh / {m.rangeWltpKm} km WLTP
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('devices.ev_model_help')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Driver Selection (only show in edit mode when drivers are available) */}
              {!isCreate && drivers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('devices.field_driver')}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <select
                      value={formData.driverId || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, driverId: e.target.value || null })
                      }
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                    >
                      <option value="">{t('devices.no_driver')}</option>
                      {drivers.map((driver) => (
                        <option key={driver.id} value={driver.id}>
                          {driver.name} {driver.phone ? `(${driver.phone})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Privacy mode (edit mode, admins only — it governs what the
                  company may legally record about its drivers). Collapsed by
                  default: this modal is already a long flat stack. */}
              {!isCreate && privacy?.canEdit && (
                <div className="border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setPrivacyOpen(!privacyOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg"
                  >
                    <span className="flex items-center text-sm font-medium text-gray-700">
                      <Shield className="h-4 w-4 mr-2 text-gray-400" />
                      Vie privée
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{privacySummaryText}</span>
                      <ChevronDown
                        className={clsx(
                          'h-4 w-4 text-gray-400 transition-transform',
                          privacyOpen && 'rotate-180'
                        )}
                      />
                    </span>
                  </button>

                  {privacyOpen && (
                    <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                      <p className="text-xs text-gray-500">
                        Pendant la plage définie, aucune position n'est enregistrée. Les
                        données ne sont pas masquées : elles ne sont jamais collectées, et
                        le réglage n'a aucun effet rétroactif.
                      </p>

                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={formData.privacyOverride}
                          onChange={(e) =>
                            setFormData({ ...formData, privacyOverride: e.target.checked })
                          }
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        Réglage spécifique à ce véhicule
                      </label>

                      {!formData.privacyOverride && (
                        <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3">
                          Ce véhicule suit les réglages de la société :{' '}
                          {privacy.defaults?.enabled && privacy.defaults.workSchedule ? (
                            <strong>{privacySummary(privacy.defaults.workSchedule)}</strong>
                          ) : (
                            <strong>mode vie privée désactivé</strong>
                          )}
                          .
                        </div>
                      )}

                      {formData.privacyOverride && (
                        <>
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={formData.privacyEnabled}
                              onChange={(e) =>
                                setFormData({ ...formData, privacyEnabled: e.target.checked })
                              }
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            Activer le mode vie privée
                          </label>

                          {formData.privacyEnabled && (
                            <>
                              <WorkScheduleEditor
                                value={formData.workSchedule}
                                onChange={(next) =>
                                  setFormData({ ...formData, workSchedule: next })
                                }
                              />

                              <div className="rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                                Résultat : {privacySummary(formData.workSchedule)}.
                              </div>

                              <p className="text-xs text-gray-500">
                                Heures exprimées dans le fuseau de la société
                                {privacy.defaults?.timezone ? ` (${privacy.defaults.timezone})` : ''}.
                              </p>
                            </>
                          )}

                          <label className="flex items-start gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={formData.privacyTheftOverride}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  privacyTheftOverride: e.target.checked,
                                })
                              }
                              className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span>
                              Continuer à enregistrer en cas de mouvement sans contact
                              <span className="block text-xs text-gray-500">
                                Protection antivol : le véhicule remorqué ou démarré sans clé
                                reste suivi malgré la plage de confidentialité.
                              </span>
                            </span>
                          </label>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Icon Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('devices.field_vehicle_type')}
                </label>
                <div className="flex gap-3">
                  {vehicleIcons.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon: item.value })}
                      className={clsx(
                        'flex-1 flex flex-col items-center p-3 rounded-lg border-2 transition-colors',
                        formData.icon === item.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <item.icon
                        className={clsx(
                          'h-6 w-6 mb-1',
                          formData.icon === item.value ? 'text-primary-600' : 'text-gray-500'
                        )}
                      />
                      <span
                        className={clsx(
                          'text-sm',
                          formData.icon === item.value ? 'text-primary-700' : 'text-gray-600'
                        )}
                      >
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('devices.field_marker_color')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, markerColor: color })}
                      className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center transition-transform',
                        formData.markerColor === color && 'ring-2 ring-offset-2 ring-gray-400'
                      )}
                      style={{ backgroundColor: color }}
                    >
                      {formData.markerColor === color && (
                        <Check className="h-4 w-4 text-white" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                {t('devices.cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? t('devices.submit_loading') : submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Reverse geocode function (via backend API → local Nominatim + Redis cache)
async function reverseGeocode(lat: number, lng: number, fallback: string): Promise<string> {
  try {
    const result = await geocodingApi.reverse(lat, lng);
    return result.data.address || fallback;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return fallback;
  }
}

// Device Detail Modal Component
function DeviceDetailModal({
  device,
  subscription,
  privacyExplanation,
  onClose,
  onEdit,
}: {
  device: Device;
  subscription: TenantSubscription | null;
  /** Why this vehicle is private, reason included when it was declared. */
  privacyExplanation?: string;
  onClose: () => void;
  onEdit: () => void;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'en' ? 'en-US' : i18n.language === 'de' ? 'de-DE' : i18n.language === 'it' ? 'it-IT' : 'fr-FR';
  const [address, setAddress] = useState<string | null>(null);
  const [loadingAddress, setLoadingAddress] = useState(false);

  useEffect(() => {
    if (device.lastPosition) {
      setLoadingAddress(true);
      reverseGeocode(device.lastPosition.lat, device.lastPosition.lng, t('devices.address_unknown'))
        .then(setAddress)
        .finally(() => setLoadingAddress(false));
    }
  }, [device.lastPosition?.lat, device.lastPosition?.lng, t]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${device.markerColor}20` }}
              >
                {device.icon === 'truck' ? (
                  <Truck className="h-6 w-6" style={{ color: device.markerColor }} />
                ) : (
                  <Car className="h-6 w-6" style={{ color: device.markerColor }} />
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{device.name}</h2>
                <p className="text-sm text-gray-500">{device.plate || t('devices.no_plate')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onEdit}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <Pencil className="h-5 w-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Status */}
            <div className="flex items-center gap-4">
              <span
                className={clsx(
                  'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                  device.privacyActive
                    ? 'bg-indigo-100 text-indigo-800'
                    : device.status === 'ONLINE'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                )}
              >
                {device.privacyActive ? (
                  <Shield className="h-4 w-4 mr-1" />
                ) : device.status === 'ONLINE' ? (
                  <Wifi className="h-4 w-4 mr-1" />
                ) : (
                  <WifiOff className="h-4 w-4 mr-1" />
                )}
                {device.privacyActive
                  ? 'Confidentialité'
                  : device.status === 'ONLINE'
                    ? t('devices.status_online')
                    : t('devices.status_offline')}
              </span>
              {device.lastPositionAt && (
                <span className="text-sm text-gray-500">
                  {t('devices.last_update_label', {
                    time: formatDistanceToNow(new Date(device.lastPositionAt), { addSuffix: true, locale: fr }),
                  })}
                </span>
              )}
            </div>

            {/* Why the vehicle is silent. Shown as a full sentence rather than a
                tooltip: in the detail view the manager is specifically looking
                for an explanation, and "no data" without a reason is what makes
                them open a ticket for a tracker that is working fine. */}
            {device.privacyActive && privacyExplanation && (
              <div className="flex items-start gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
                <Shield className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{privacyExplanation}</span>
              </div>
            )}

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <InfoCard
                icon={Activity}
                label={t('devices.info_imei')}
                value={device.imei}
                mono
              />
              <InfoCard
                icon={Truck}
                label={t('devices.info_vehicle')}
                value={`${device.brand || '-'} ${device.model || ''}`}
              />
              <InfoCard
                icon={Gauge}
                label={t('devices.info_speed_limit')}
                value={`${device.speedLimit || 120} km/h`}
              />
              <InfoCard
                icon={Activity}
                label={t('devices.info_group')}
                value={device.group?.name || '-'}
              />
            </div>

            {/* EV section */}
            {device.evEnabled && <EvDevicePanel deviceId={device.id} />}

            {/* Subscription Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                {t('devices.subscription_section')}
              </h3>
              {subscription ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">{t('devices.subscription_plan')}</p>
                    <p className="text-sm font-medium text-gray-900">{subscription.plan.displayName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('devices.subscription_status')}</p>
                    <p className={clsx('text-sm font-medium', subscription.status === 'ACTIVE' ? 'text-green-600' : subscription.status === 'TRIALING' ? 'text-blue-600' : 'text-gray-900')}>
                      {subscription.status === 'ACTIVE' ? t('devices.subscription_status_active') : subscription.status === 'TRIALING' ? t('devices.subscription_status_trial') : subscription.status}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">{t('devices.subscription_expiration')}</p>
                    <p className="text-sm font-medium text-gray-900">
                      {subscription.expiresAt
                        ? new Date(subscription.expiresAt).toLocaleDateString(dateLocale)
                        : subscription.currentPeriodEnd
                          ? new Date(subscription.currentPeriodEnd).toLocaleDateString(dateLocale)
                          : '-'}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400">{t('devices.no_subscription')}</p>
              )}
            </div>

            {/* Last Position */}
            {device.lastPosition && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">{t('devices.last_position_title')}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">{t('devices.pos_speed')}</p>
                    <p
                      className={clsx(
                        'text-lg font-semibold',
                        device.lastPosition.speed > (device.speedLimit || 120)
                          ? 'text-red-600'
                          : 'text-gray-900'
                      )}
                    >
                      {Math.round(device.lastPosition.speed)} km/h
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('devices.pos_heading')}</p>
                    <p className="text-lg font-semibold text-gray-900 flex items-center">
                      <Navigation
                        className="h-4 w-4 mr-1"
                        style={{ transform: `rotate(${device.lastPosition.heading}deg)` }}
                      />
                      {device.lastPosition.heading}°
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('devices.pos_ignition')}</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {device.lastPosition.ignition ? t('devices.pos_ignition_on') : t('devices.pos_ignition_off')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('devices.pos_altitude')}</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {device.lastPosition.altitude || 0} m
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {loadingAddress ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t('devices.loading_address')}
                          </span>
                        ) : address ? (
                          address
                        ) : (
                          `${device.lastPosition.lat.toFixed(6)}, ${device.lastPosition.lng.toFixed(6)}`
                        )}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {device.lastPosition.lat.toFixed(6)}, {device.lastPosition.lng.toFixed(6)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {new Date(device.lastPosition.timestamp).toLocaleString(dateLocale)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!device.lastPosition && (
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <MapPin className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">{t('devices.no_position')}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              {t('devices.close')}
            </button>
            <a
              href={`/map?device=${device.id}`}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-center"
            >
              {t('devices.view_on_map')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Info Card Component
function InfoCard({
  icon: Icon,
  label,
  value,
  mono = false,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="p-2 bg-white rounded-lg">
        <Icon className="h-5 w-5 text-gray-500" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className={clsx('text-sm font-medium text-gray-900', mono && 'font-mono')}>
          {value}
        </p>
      </div>
    </div>
  );
}

function socColor(soc: number | null): { text: string; bar: string } {
  if (soc === null) return { text: 'text-gray-500', bar: 'bg-gray-300' };
  if (soc <= 20) return { text: 'text-red-700', bar: 'bg-red-500' };
  if (soc <= 40) return { text: 'text-amber-700', bar: 'bg-amber-500' };
  return { text: 'text-emerald-700', bar: 'bg-emerald-500' };
}

function EvDevicePanel({ deviceId }: { deviceId: string }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'en' ? 'en-US' : i18n.language === 'de' ? 'de-DE' : i18n.language === 'it' ? 'it-IT' : 'fr-FR';
  const snap = useQuery<EvDeviceSnapshot>({
    queryKey: ['ev-device', deviceId],
    queryFn: () => evApi.getDevice(deviceId),
    refetchInterval: 60_000,
  });
  const sessions = useQuery<EvChargeSession[]>({
    queryKey: ['ev-sessions', deviceId],
    queryFn: () => evApi.listSessions(deviceId, 10),
    refetchInterval: 120_000,
  });
  const timeline = useQuery<EvSocPoint[]>({
    queryKey: ['ev-soc-timeline', deviceId],
    queryFn: () => evApi.getSocTimeline(deviceId, 7),
  });

  if (snap.isLoading) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-4 text-sm text-gray-500">
        {t('devices.ev_loading')}
      </div>
    );
  }
  if (snap.isError || !snap.data) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
        {t('devices.ev_unavailable')}
      </div>
    );
  }

  const d = snap.data;
  const colors = socColor(d.currentSoc);

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
          <BatteryCharging className="h-4 w-4" />
          {t('devices.ev_module_section')}
        </h3>
        {d.currentChargingState === 1 && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
            <Zap className="h-3 w-3" /> {t('devices.ev_charging_badge')}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <p className="text-xs text-gray-500">{t('devices.ev_battery_level')}</p>
          <p className={clsx('text-lg font-semibold tabular-nums', colors.text)}>
            {d.currentSoc !== null ? `${d.currentSoc}%` : '—'}
          </p>
          <div className="mt-1 h-1.5 w-24 bg-gray-200 rounded">
            <div className={clsx('h-1.5 rounded', colors.bar)} style={{ width: `${d.currentSoc ?? 0}%` }} />
          </div>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('devices.ev_estimated_range')}</p>
          <p className="text-lg font-semibold text-gray-900 tabular-nums">
            {d.estimatedRangeKm !== null ? `${d.estimatedRangeKm} km` : '—'}
          </p>
        </div>
        <div className="col-span-2">
          <p className="text-xs text-gray-500">{t('devices.ev_model_section')}</p>
          {d.evModel ? (
            <p className="text-sm font-medium text-gray-900">
              {d.evModel.make} {d.evModel.model}
              {d.evModel.variant ? ` ${d.evModel.variant}` : ''}
              <span className="ml-2 text-xs text-gray-400">
                {d.evModel.batteryKwh} kWh · {d.evModel.rangeWltpKm} km WLTP
              </span>
            </p>
          ) : (
            <p className="text-sm text-amber-700">{t('devices.ev_model_undefined')}</p>
          )}
        </div>
      </div>

      {timeline.data && timeline.data.length > 1 && (
        <div>
          <p className="text-xs text-gray-500 mb-1">{t('devices.ev_battery_7d')}</p>
          <SocSparkline points={timeline.data} />
        </div>
      )}

      <div>
        <p className="text-xs text-gray-500 mb-1">{t('devices.ev_recent_sessions')}</p>
        {sessions.isLoading ? (
          <p className="text-sm text-gray-400">{t('devices.ev_loading_sessions')}</p>
        ) : !sessions.data || sessions.data.length === 0 ? (
          <p className="text-sm text-gray-400">{t('devices.ev_no_sessions')}</p>
        ) : (
          <div className="space-y-1">
            {sessions.data.slice(0, 5).map((s) => (
              <ChargeSessionRow key={s.id} s={s} dateLocale={dateLocale} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SocSparkline({ points }: { points: EvSocPoint[] }) {
  if (points.length < 2) return null;
  const w = 320;
  const h = 60;
  const min = 0;
  const max = 100;
  const t0 = new Date(points[0].timestamp).getTime();
  const t1 = new Date(points[points.length - 1].timestamp).getTime();
  const span = Math.max(1, t1 - t0);
  const path = points
    .map((p, i) => {
      const x = ((new Date(p.timestamp).getTime() - t0) / span) * w;
      const y = h - ((p.soc - min) / (max - min)) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14 bg-white rounded border border-gray-100">
      <path d={path} fill="none" stroke="#10b981" strokeWidth="1.5" />
      <line x1="0" y1={h * 0.8} x2={w} y2={h * 0.8} stroke="#fca5a5" strokeDasharray="2 2" strokeWidth="0.5" />
    </svg>
  );
}

function ChargeSessionRow({ s, dateLocale }: { s: EvChargeSession; dateLocale: string }) {
  const start = new Date(s.startedAt);
  const end = s.endedAt ? new Date(s.endedAt) : null;
  const durationMin = end ? Math.round((end.getTime() - start.getTime()) / 60000) : null;
  return (
    <div className="flex items-center justify-between text-xs bg-white rounded px-2 py-1.5">
      <div className="flex-1 min-w-0">
        <span className="text-gray-700">
          {start.toLocaleString(dateLocale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
        {s.geocodedAddress && (
          <span className="block text-gray-400 truncate">{s.geocodedAddress}</span>
        )}
      </div>
      <div className="flex items-center gap-3 ml-3">
        {s.startSoc !== null && s.endSoc !== null && (
          <span className="text-gray-700 tabular-nums">
            {s.startSoc} → {s.endSoc}%
          </span>
        )}
        {s.energyKwh !== null && (
          <span className="text-gray-500 tabular-nums">{s.energyKwh.toFixed(1)} kWh</span>
        )}
        {durationMin !== null && (
          <span className="text-gray-400">{durationMin} min</span>
        )}
        {s.interrupted && (
          <span className="text-amber-700 text-[10px] font-medium">interrompue</span>
        )}
        {!s.endedAt && (
          <span className="text-emerald-700 text-[10px] font-medium">en cours</span>
        )}
      </div>
    </div>
  );
}
